import { FC, useMemo, useState } from 'react';
import { max, sort } from 'd3';

import { NodeStatus } from '@dbt-labs/dbt-dag';
import {
  CellContext,
  Link,
  Pagination,
  SortingState,
  Table,
} from '@dbt-labs/sourdough';

import { NodeStatusCell } from '@/components/Table/NodeStatusCell';
import { useAdminEnvironment } from '@/hooks/admin/environments';
import { useModelExecutionRunInfo } from '@/hooks/discovery/modelExecutionRunInfo';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { formatAbsoluteLocalDate } from '@/util/date';
import { generateDurationFormatter } from '@/util/formatDuration';

const PAGE_SIZE = 10;

export interface ExecutionRunTableProps {
  accountId: number;
  projectId: number;
  environmentId: number;
  uniqueId: string;
  startDate: string;
  endDate: string;
  status?: NodeStatus;
}

export const ExecutionRunTable: FC<ExecutionRunTableProps> = ({
  accountId,
  projectId,
  environmentId,
  uniqueId,
  startDate,
  endDate,
  status,
}) => {
  const [sortDir, setSortDir] = useState('desc');
  const [sortCol, setSortCol] = useState('completedAtDate');
  const [currentPage, setCurrentPage] = useState(0);
  const projectScope = useUrlProjectParams();
  const getRunLink = useLinkToDbtCloud('runDetails');

  const [runInfo] = useModelExecutionRunInfo({
    startDate,
    endDate,
    environmentId: environmentId,
    uniqueId: uniqueId,
  });

  const environment = useAdminEnvironment(environmentId, accountId, undefined);
  const jobNamesById = (environment?.data?.jobs || []).reduce<Record<number, string>>(
    (acc, job) => {
      acc[job.id] = job.name;
      return acc;
    },
    {},
  );

  const data = useMemo(() => {
    const maxTimeSec =
      max(runInfo.data?.performance?.modelExecutionHistory || [], (d) =>
        max(d.executionsByJob ?? [], (d) =>
          max(d.executions ?? [], (x) => x.executionTime),
        ),
      ) || 0;
    const formatDuration = generateDurationFormatter('s', maxTimeSec);

    const modelExecutionHistory =
      runInfo.data?.performance?.modelExecutionHistory
        ?.flatMap((x) => x.executionsByJob)
        ?.flatMap((x) =>
          x?.executions?.map((e) => {
            const completedAt = new Date(e.executionStartedAt);
            completedAt.setSeconds(completedAt.getSeconds() + e.executionTime);
            return {
              jobName: jobNamesById[x.jobId],
              command: e.isFullRefresh ? 'Full Refresh' : 'Partial',
              completedAtDate: completedAt,
              completedAt: formatAbsoluteLocalDate(completedAt),
              duration: formatDuration.humanize('s', e.executionTime),
              ...e,
            };
          }),
        ) || [];

    const sortedHistory = sort(modelExecutionHistory, (x: any) => x[sortCol]);
    if (sortDir === 'desc') {
      sortedHistory.reverse();
    }
    return sortedHistory.filter((x) => {
      return !status || x?.status === status;
    });
  }, [
    runInfo.data?.performance?.modelExecutionHistory,
    sortDir,
    jobNamesById,
    sortCol,
    status,
  ]);

  // Reset to first page when data, sort, or filter changes
  const dataKey = `${startDate}-${endDate}-${sortCol}-${sortDir}-${status}-${data.length}`;
  const [prevDataKey, setPrevDataKey] = useState(dataKey);
  if (dataKey !== prevDataKey) {
    setPrevDataKey(dataKey);
    setCurrentPage(0);
  }

  const paginatedData = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    return data.slice(start, start + PAGE_SIZE);
  }, [data, currentPage]);

  return (
    <>
      <Table
        isLoading={runInfo.fetching || environment.isLoading}
        data={paginatedData}
        loadingRowCount={PAGE_SIZE}
        columns={[
          {
            header: 'Job Run',
            id: 'jobName',
            enableSorting: false,
            minSize: 200,
            size: 6,
            // TODO: figure out a way to use LinkCell
            // ex. ModelFilterView uses LinkCell for model name but it somehow links
            cell: ({ row }: CellContext<any, unknown>) => {
              const runId = row?.original?.runId || 0;
              const jobName = row?.original?.jobName || '';
              return getRunLink ? (
                <Link
                  isInternal
                  className={'overflow-hidden text-ellipsis'}
                  to={getRunLink({
                    ...projectScope,
                    runId,
                    projectId,
                  })}
                >
                  {jobName}
                </Link>
              ) : (
                <span className="overflow-hidden text-ellipsis">{jobName}</span>
              );
            },
          },
          {
            header: 'Model Status',
            accessorKey: 'status',
            id: 'status',
            cell: NodeStatusCell,
            enableSorting: false,
            minSize: 100,
            size: 1,
          },
          {
            header: 'Command',
            accessorKey: 'command',
            id: 'command',
            enableSorting: false,
            minSize: 110,
            size: 1,
          },
          {
            header: 'Completed at',
            accessorKey: 'completedAt',
            id: 'completedAtDate',
            enableSorting: true,
            minSize: 200,
            size: 2,
          },
          {
            header: `Duration`,
            accessorKey: 'duration',
            id: 'executionTime',
            enableSorting: true,
            minSize: 200,
            size: 2,
          },
        ]}
        initialSortColumn="completedAtDate"
        initialSortDesc={true}
        rowHeight={52}
        onChangeSort={(sortRule: SortingState) => {
          if (!sortRule?.length) {
            return;
          }
          const sortCol: string = sortRule[0].id;
          const sortDir = sortRule[0].desc ? 'desc' : 'asc';
          setSortCol(sortCol);
          setSortDir(sortDir);
        }}
      />
      {data.length > PAGE_SIZE && (
        <Pagination
          onPageChange={setCurrentPage}
          totalRows={data.length}
          currentPage={currentPage}
          rowsPerPage={PAGE_SIZE}
        />
      )}
    </>
  );
};
